import GroupIcon from "@mui/icons-material/Group";
import { AlertTitle, Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";

import { SessionContext } from "../../../Context/SessionContext";
import { emailIsValid, phoneIsValid } from "../../../Utils/Helpers";
import { emailValidation } from "../../../Utils/ValidationSchema";
import AlertGridItem from "../../Helpers/AlertGridItem";
import { ValidatedTextField } from "../../Helpers/ValidatedTextField";
import StepperItem from "../StepperItem";

const Booking: React.FC = () => {
  const {
    apptErrorCenters,
    bookingDisquals,
    customer,
    duplicateCustomers,
    isSavingCustomer,
    unverifiedCustomer,
    setCustomer,
    setSaveCustomer,
    setShowDuplicateModal,
    setShowBookingWizard,
    setUnverifiedCustomer
  } = useContext(SessionContext);
  const [hasDuplicateConflicts, setHasDuplicateConflicts] = useState(false);
  const customerIdRef = useRef(customer.id);

  // ON CUSTOMER ID CHANGING
  useEffect(() => {
    // open the booking wizard seamlessly if the customer is successfully POSTed
    if (!customerIdRef.current && customer.id) setShowBookingWizard(true);
    customerIdRef.current = customer.id;
  }, [customer.id, customer.distanceToCenter, customer.phoneNumber, setShowBookingWizard]);

  // ON CUSTOMER PRIMARIES OR DUPLICATES CHANGING
  useEffect(
    () =>
      // Identify if current customer conflicts with current duplicate records
      setHasDuplicateConflicts(
        !!duplicateCustomers.length &&
          duplicateCustomers.some(
            dc =>
              dc.firstName === customer.firstName ||
              dc.lastName === customer.lastName ||
              dc.phoneNumber === customer.phoneNumber ||
              dc.email === customer.email
          )
      ),
    [duplicateCustomers, customer.firstName, customer.lastName, customer.phoneNumber, customer.email]
  );

  const allDisquals = [...bookingDisquals];
  if (!customer.zipCode) allDisquals.push("Missing Zip Code");
  if (!customer.id) {
    if (!customer.firstName) allDisquals.push("Missing First Name");
    if (!customer.lastName) allDisquals.push("Missing Last Name");
    if (!emailIsValid(customer.email) && !phoneIsValid(customer.phoneNumber))
      allDisquals.push("Missing Valid Mobile Phone or Email");
  }

  const handleClick = () => {
    if (!customer.id) setSaveCustomer({ override: false, customer });
    else setShowBookingWizard(true);
  };

  const centerDisabled = apptErrorCenters.some(id => id === customer.centerId);
  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <StepperItem>
          <Typography variant="body2">
            Great, thank you! How long have you been thinking about a procedure like this?
          </Typography>
        </StepperItem>
      </Grid>
      <Grid item>
        <StepperItem>
          <Typography variant="body2">
            <Typography variant="stageDirection" component="span">
              If &quot;New Thoughts&quot; :
            </Typography>
            <Box component="span" ml={1}>
              <Typography variant="body2" component="span">
                Then, I&apos;m glad you found us! We can help you start your transformation right away.
              </Typography>
            </Box>
          </Typography>
        </StepperItem>
      </Grid>
      <Grid item>
        <StepperItem>
          <Typography variant="body2">
            <Typography variant="stageDirection" component="span">
              If &quot;Long Time&quot; :
            </Typography>
            <Box component="span" ml={1}>
              <Typography variant="body2" component="span">
                I&apos;m glad you finally decided to give us a call! You are going to be glad that did.
              </Typography>
            </Box>
          </Typography>
        </StepperItem>
      </Grid>
      <Grid item>
        <StepperItem>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Typography variant="stageDirection">
                While the patient is responding to your open-ended question, open the Booking Wizard to locate the first
                available consult!
              </Typography>
            </Grid>
            <Grid container direction="column" item xs alignItems="center" spacing={1}>
              <AlertGridItem severity="error" in={Boolean(allDisquals.length)} spacing={2}>
                <AlertTitle>Booking is disabled for these reasons:</AlertTitle>
                {allDisquals.map((t, key) => (
                  <div key={key}>{t}</div>
                ))}
              </AlertGridItem>
              <AlertGridItem
                severity="error"
                in={!allDisquals.length && Boolean(duplicateCustomers.length) && hasDuplicateConflicts}
                spacing={2}
                action={
                  <Button
                    color="inherit"
                    disabled={isSavingCustomer}
                    startIcon={<GroupIcon />}
                    onClick={() => setShowDuplicateModal(true)}
                  >
                    Duplicates
                  </Button>
                }
              >
                <AlertTitle>{`${duplicateCustomers.length} duplicate ${
                  duplicateCustomers.length === 1 ? "guest has" : "guests have"
                } been found. Please either:`}</AlertTitle>
                <div>Resolve the conflict.</div>
                <div>Update the highlighted fields in the ribbon.</div>
              </AlertGridItem>
              <AlertGridItem severity="error" in={centerDisabled} spacing={2}>
                There was an error in the booking process. Please resolve the actions in the bottom left to continue.
              </AlertGridItem>
              <Grid item>
                {isSavingCustomer ? (
                  <CircularProgress />
                ) : (
                  <Button
                    onClick={handleClick}
                    disabled={!!allDisquals.length || centerDisabled || Boolean(duplicateCustomers.length)}
                  >
                    Open Booking Wizard
                  </Button>
                )}
              </Grid>
            </Grid>
            <Grid item>
              <Typography variant="stageDirection" component="span">
                After responding to patient’s answer:
              </Typography>
              <Box component="span" ml={1}>
                <Typography variant="body2" component="span">
                  Let’s get you set up for that consultation at our closest location,{" "}
                  <strong>{customer.center?.name}</strong>! Our first available is...
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </StepperItem>
      </Grid>
      <Grid item>
        <StepperItem>
          <Typography variant="body2">Let me confirm the spelling of your name:</Typography>
          <Typography variant="body2">
            <strong>{`${customer.firstName} ${customer.lastName}`}</strong>
          </Typography>
        </StepperItem>
      </Grid>
      <Grid item>
        <StepperItem>
          <Typography variant="body2">
            And what&apos;s a good email address to send your appointment confirmation? It includes a link with driving
            directions and all the appointment details.
          </Typography>
        </StepperItem>
      </Grid>
      <Grid item>
        <StepperItem>
          <ValidatedTextField
            label="Email"
            value={unverifiedCustomer.email || undefined}
            onChange={email => setUnverifiedCustomer(c => ({ ...c, email: email || "" }))}
            onAccepted={email => setCustomer(c => ({ ...c, email: email?.trim() || null }))}
            {...emailValidation}
          />
        </StepperItem>
      </Grid>
      <Grid item>
        <StepperItem>
          <Typography variant="body2">
            I am so glad you decided to schedule with us today and start your transformation journey! We look forward to
            seeing you and will remind you with a text message the day before your appointment!
          </Typography>
        </StepperItem>
      </Grid>
    </Grid>
  );
};

export default Booking;
